import axios from "axios";

/**
 * Was this rejection our own cancellation rather than a failure?
 *
 * For the caller that has a render state to set on a real failure and so
 * cannot simply absorb it — see `AuditTrail` and `useCompanyDiagnostics`.
 */
export const isCancellation = (error: unknown): boolean =>
  axios.isCancel(error);

/**
 * Absorb a rejection that has nowhere to go.
 *
 * **Only where the record's own absence is already terminal.** That is the
 * whole precondition, and getting it wrong is what this feature's longest-
 * running frontend defect was: a screen holding `T | null` says "failed" by
 * still being null once the request has settled, but a screen holding `T[]`
 * says nothing at all — `[]` is equally what a successful load of nothing
 * leaves behind. Absorbing there told a coach the system was empty when it was
 * broken. A collection needs a `LoadStatus`; see `utils/loadStatus.ts`.
 *
 * Two kinds arrive here and neither has a consumer:
 *
 *   - **Cancellations.** These screens abort the in-flight request whenever the
 *     query changes or the component unmounts, and axios rejects the aborted
 *     promise. Nothing went wrong and there is nothing to tell the user.
 *   - **Real failures.** The global axios interceptor has *already* surfaced
 *     these — 401 to login, 403 to forbidden, everything else a toast. No
 *     component may catch to build its own error UI (D10), so by the time the
 *     rejection reaches a `.catch()` here the user has been told and there is
 *     no second action to take.
 *
 * Rethrowing was the bug: `.finally()` propagates the rejection, so a rethrow
 * with no downstream `.catch()` is just an unhandled promise rejection with
 * extra steps — a noisy console and, under a strict runtime, a crash.
 */
export const absorbRejection = (): void => undefined;
